import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { resolveRichText } from '@vibesites/runtime';
import {
  useSlotGroup,
  useGallery,
  useProperty,
  useContentToken,
  RichTextContent,
  EditableSlotGroup,
  EditableToken,
} from '@vibesites/runtime/react';
import type { Floorplan, PhotoGallery } from '@vibesites/schema';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { GlowTrace } from './GlowTrace';

// ============================================================================
// Bedroom options label (for property_bedrooms_range field tokens)
// ============================================================================

/**
 * Parse a beds value ("0", "studio", "2") into a numeric count (0 = studio).
 */
function parseBedCount(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim().toLowerCase();
  if (!trimmed) return null;
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 0;
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num < 0) return null;
  return num;
}

/**
 * Collect unique bedroom counts from floor plans, sorted ascending.
 */
function bedCountsFromFloorplans(floorplans: Floorplan[] | null | undefined): number[] {
  if (!floorplans?.length) return [];
  const beds = new Set<number>();
  for (const plan of floorplans) {
    const count = parseBedCount(plan.floorplan_beds_min);
    if (count != null) beds.add(count);
  }
  return [...beds].sort((a, b) => a - b);
}

/**
 * Expand a raw bedrooms-range string ("2 - 2", "studio to 3") into discrete
 * bed counts. Equal endpoints collapse to one; unequal endpoints expand as a
 * contiguous inclusive range when both ends parse cleanly.
 */
function bedCountsFromRange(raw: string | null | undefined): number[] {
  if (!raw) return [];
  const trimmed = String(raw).trim();
  if (!trimmed) return [];

  const parts = trimmed
    .split(/\s*(?:[-–—]|to)\s*/i)
    .map((p) => parseBedCount(p))
    .filter((n): n is number => n != null);

  if (!parts.length) return [];
  if (parts.length === 1) return parts;

  const min = Math.min(...parts);
  const max = Math.max(...parts);
  if (min === max) return [min];

  const beds: number[] = [];
  for (let i = min; i <= max; i++) beds.push(i);
  return beds;
}

/**
 * Format bedroom options for display / SEO — numbers (and Studio) only:
 *   [2]           → "2"
 *   [0]           → "Studio"
 *   [0, 1, 2, 3]  → "Studio, 1, 2 & 3"
 */
function formatBedroomOptionsList(beds: number[]): string | null {
  if (!beds.length) return null;

  if (beds.length === 1) {
    const only = beds[0];
    return only === 0 ? 'Studio' : String(only);
  }

  const parts = beds.map((b) => (b === 0 ? 'Studio' : String(b)));
  if (parts.length === 2) return `${parts[0]} & ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} & ${parts[parts.length - 1]}`;
}

/**
 * Prefer distinct bedroom types present on floor plans; fall back to expanding
 * the property_bedrooms_range scalar when plans are unavailable.
 */
function resolveBedroomOptionsLabel(
  floorplans: Floorplan[] | null | undefined,
  range: string | null | undefined,
): string | null {
  return (
    formatBedroomOptionsList(bedCountsFromFloorplans(floorplans)) ||
    formatBedroomOptionsList(bedCountsFromRange(range))
  );
}

// ============================================================================
// Smart Photo Selection
// ============================================================================

const CARD_KEYWORDS: Record<string, string[]> = {
  floorplans: ['interior', 'apartment', 'unit', 'bedroom', 'living room', 'kitchen', 'bathroom', 'floor plan', 'model', 'home', 'layout', 'spacious'],
  gallery: ['community', 'building', 'property', 'exterior', 'entrance', 'lobby', 'leasing', 'welcome', 'amenity', 'pool', 'lounge'],
  amenities: ['pool', 'gym', 'fitness', 'clubhouse', 'lounge', 'amenity', 'recreation', 'courtyard', 'grill', 'playground', 'spa', 'theater'],
};

function scorePhoto(photo: PhotoGallery, keywords: string[]): number {
  const searchText = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!searchText) return 0;
  return keywords.reduce((score, kw) => score + (searchText.includes(kw) ? 1 : 0), 0);
}

function findBestPhotoIndex(
  gallery: PhotoGallery[],
  cardKey: string,
  usedIndices: Set<number>,
  fallbackIndex: number,
): number {
  const keywords = CARD_KEYWORDS[cardKey] || [];
  if (!gallery.length || !keywords.length) return fallbackIndex;

  let bestIndex = -1;
  let bestScore = 0;

  for (let i = 0; i < gallery.length; i++) {
    if (usedIndices.has(i)) continue;
    const score = scorePhoto(gallery[i], keywords);
    if (score > bestScore) {
      bestScore = score;
      bestIndex = i;
    }
  }

  if (bestIndex >= 0) return bestIndex;

  for (let i = fallbackIndex; i < gallery.length; i++) {
    if (!usedIndices.has(i)) return i;
  }
  for (let i = 0; i < fallbackIndex; i++) {
    if (!usedIndices.has(i)) return i;
  }

  return Math.min(fallbackIndex, Math.max(gallery.length - 1, 0));
}

function useSmartPhotoIndices(): [number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const used = new Set<number>();
    const keys = ['floorplans', 'gallery', 'amenities'] as const;
    const fallbacks = [1, 3, 5];
    const indices: number[] = [];
    for (let i = 0; i < keys.length; i++) {
      const idx = findBestPhotoIndex(gallery, keys[i], used, fallbacks[i]);
      used.add(idx);
      indices.push(idx);
    }
    return indices as [number, number, number];
  }, [gallery]);
}

function makeSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// ============================================================================
// Static slot group definitions (for contentSchema)
// ============================================================================

const card1ImageDef: SlotGroupDefinition = {
  label: 'Floor Plans Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card2ImageDef: SlotGroupDefinition = {
  label: 'Gallery Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card3ImageDef: SlotGroupDefinition = {
  label: 'Amenities Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 5, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const card1LinkDef: SlotGroupDefinition = {
  label: 'Floor Plans Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Choose Your Floorplan' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card2LinkDef: SlotGroupDefinition = {
  label: 'Gallery Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/gallery' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'Browse The Gallery' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const card3LinkDef: SlotGroupDefinition = {
  label: 'Amenities Link',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Button Text', type: 'text', defaultValue: 'View The Amenities' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// CTA Card Sub-component
// ============================================================================

function CTACard({
  imageUrl,
  imageAlt,
  titleNode,
  descNode,
  linkNode,
  glowDelay = 0.3,
}: {
  imageUrl?: string | null;
  imageAlt?: string | null;
  titleNode: React.ReactNode;
  descNode: React.ReactNode;
  linkNode: React.ReactNode;
  glowDelay?: number;
}) {
  const imgCls = 'absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105';
  return (
    <div className="cta-card glow-trace-wrap relative overflow-hidden group h-full">
      <GlowTrace delay={glowDelay} />
      {imageUrl ? (
        <VibesitesImage url={imageUrl} alt={imageAlt || ''} className={imgCls} sizes="(min-width: 1024px) 33vw, 100vw" />
      ) : (
        <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
      )}
      <div className="absolute inset-0 cta-card-overlay" />
      <div className="cta-card-content relative z-10 flex flex-col justify-end h-full">
        {titleNode}
        {descNode}
        {linkNode}
      </div>
    </div>
  );
}

// ============================================================================
// Component
// ============================================================================

export function CTASection() {
  const property = useProperty();
  const [idx1, idx2, idx3] = useSmartPhotoIndices();

  const def1 = useMemo(() => makeSlotDef('Floor Plans Image', idx1), [idx1]);
  const def2 = useMemo(() => makeSlotDef('Gallery Image', idx2), [idx2]);
  const def3 = useMemo(() => makeSlotDef('Amenities Image', idx3), [idx3]);

  const img1 = useSlotGroup('ctasection.card1_image', def1);
  const img2 = useSlotGroup('ctasection.card2_image', def2);
  const img3 = useSlotGroup('ctasection.card3_image', def3);

  const link1 = useSlotGroup('ctasection.card1_link', card1LinkDef);
  const link2 = useSlotGroup('ctasection.card2_link', card2LinkDef);
  const link3 = useSlotGroup('ctasection.card3_link', card3LinkDef);

  // Expand property_bedrooms_range into listed bedroom numbers so embedded
  // field tokens read "2" / "Studio, 1, 2 & 3" instead of raw ranges like "2 - 2".
  const bedroomOptionsLabel = useMemo(
    () =>
      resolveBedroomOptionsLabel(
        property.floorplans,
        property.property_bedrooms_range,
      ),
    [property.floorplans, property.property_bedrooms_range],
  );

  const card1DescRaw = useContentToken('ctasection.card1.description');
  const card1DescResolved = useMemo(() => {
    if (!card1DescRaw) return '';
    const resolveProperty = {
      ...(property as Record<string, unknown>),
      property_bedrooms_range:
        bedroomOptionsLabel ?? property.property_bedrooms_range ?? '',
    };
    let html = resolveRichText(card1DescRaw, resolveProperty as Record<string, any>);
    // Mirror RichTextContent: strip a single wrapping <p> when rendering as="p".
    const trimmed = html.trim();
    const wrap = trimmed.match(/^<p>([\s\S]*)<\/p>$/);
    if (wrap && !/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(wrap[1])) {
      html = wrap[1];
    }
    return html;
  }, [card1DescRaw, property, bedroomOptionsLabel]);

  useRevealStyles();
  const gridRef = useScrollReveal<HTMLDivElement>({ threshold: 0.3 });

  const titleCls = 'cta-card-title font-heading font-bold uppercase text-white mb-2 drop-shadow-lg';
  const descCls = 'cta-card-desc font-body text-white mb-4 leading-relaxed drop-shadow-md';
  const btnCls = 'cta-card-btn inline-flex items-center justify-center self-start';

  return (
    <section className="w-full bg-white">
      <div ref={gridRef as React.RefObject<HTMLDivElement>} className="cta-grid reveal-fade-up grid grid-cols-1 md:grid-cols-3 gap-2 p-2">
        <EditableSlotGroup groupPath="ctasection.card1_image" definition={def1} className="cta-slot">
          <CTACard
            imageUrl={img1.image}
            imageAlt={img1.alt}
            titleNode={
              <RichTextContent path="ctasection.card1.title" label="Card 1 Title" defaultValue="<p>Spacious Layouts</p>" as="h2" className={titleCls} />
            }
            descNode={
              <EditableToken
                path="ctasection.card1.description"
                type="richtext"
                label="Card 1 Description"
                as="p"
                className={descCls}
              >
                <span
                  data-vibesite-richtext=""
                  dangerouslySetInnerHTML={{ __html: card1DescResolved }}
                  style={{ display: 'contents' }}
                />
              </EditableToken>
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card1_link" definition={card1LinkDef}>
                <a href={link1.url || '#'} target={link1._target || '_self'} className={`${btnCls} cta-card-btn--card1`}>
                  {link1.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.3}
          />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="ctasection.card2_image" definition={def2} className="cta-slot">
          <CTACard
            imageUrl={img2.image}
            imageAlt={img2.alt}
            titleNode={
              <RichTextContent path="ctasection.card2.title" label="Card 2 Title" defaultValue="<p>Picture Yourself Here</p>" as="h2" className={titleCls} />
            }
            descNode={
              <RichTextContent path="ctasection.card2.description" label="Card 2 Description" defaultValue="<p>See what life at our community is all about.</p>" as="p" className={descCls} />
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card2_link" definition={card2LinkDef}>
                <a href={link2.url || '#'} target={link2._target || '_self'} className={`${btnCls} cta-card-btn--card2`}>
                  {link2.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.6}
          />
        </EditableSlotGroup>
        <EditableSlotGroup groupPath="ctasection.card3_image" definition={def3} className="cta-slot">
          <CTACard
            imageUrl={img3.image}
            imageAlt={img3.alt}
            titleNode={
              <RichTextContent path="ctasection.card3.title" label="Card 3 Title" defaultValue="<p>Convenience All Around</p>" as="h2" className={titleCls} />
            }
            descNode={
              <RichTextContent path="ctasection.card3.description" label="Card 3 Description" defaultValue="<p>Life is easier at our community.</p>" as="p" className={descCls} />
            }
            linkNode={
              <EditableSlotGroup groupPath="ctasection.card3_link" definition={card3LinkDef}>
                <a href={link3.url || '#'} target={link3._target || '_self'} className={`${btnCls} cta-card-btn--card3`}>
                  {link3.label || 'Learn More'}
                </a>
              </EditableSlotGroup>
            }
            glowDelay={0.9}
          />
        </EditableSlotGroup>
      </div>

      <style>{`
        .cta-slot {
          display: block;
          height: 100%;
          min-width: 0;
        }
        .cta-card {
          height: 28vh;
          min-height: 200px;
          max-height: 320px;
          border-radius: 4px;
        }
        .cta-card-content {
          padding: 1.25rem;
          gap: 0;
          min-width: 0;
        }
        .cta-card-title {
          font-size: 0.875rem;
          letter-spacing: 0.12em;
          line-height: 1.3;
        }
        .cta-card-desc {
          font-size: 0.8125rem;
          line-height: 1.45;
          display: -webkit-box;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
          overflow: hidden;
        }
        .cta-card-overlay {
          background: linear-gradient(
            to top,
            color-mix(in srgb, var(--vibe-color-primary) 55%, black) 0%,
            color-mix(in srgb, var(--vibe-color-primary) 30%, rgba(0,0,0,0.5)) 40%,
            color-mix(in srgb, var(--vibe-color-primary) 10%, rgba(0,0,0,0.15)) 70%,
            transparent 100%
          );
        }
        .cta-card-btn {
          padding: 0.5rem 1.25rem;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          color: var(--vibe-color-text);
          background: white;
          border-radius: 9999px;
          transition: all 0.3s ease;
          white-space: normal;
          text-align: center;
          max-width: 100%;
          line-height: 1.3;
        }
        .cta-card-btn:hover {
          background: var(--vibe-color-primary);
          color: var(--vibe-color-text-on-primary);
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }

        @media (min-width: 768px) {
          .cta-card-content {
            padding: 1.25rem;
          }
          .cta-card-title {
            font-size: 0.8125rem;
            letter-spacing: 0.12em;
          }
          .cta-card-desc {
            font-size: 0.75rem;
            -webkit-line-clamp: 3;
            margin-bottom: 0.75rem;
          }
          .cta-card-btn {
            padding: 0.45rem 1rem;
            font-size: 0.6875rem;
            white-space: normal;
          }
        }

        @media (min-width: 1024px) {
          .cta-card-content {
            padding: 1.5rem 1.75rem;
          }
          .cta-card-title {
            font-size: 1rem;
            letter-spacing: 0.15em;
          }
          .cta-card-desc {
            font-size: 0.875rem;
            -webkit-line-clamp: 2;
            margin-bottom: 1rem;
          }
          .cta-card-btn {
            padding: 0.5rem 1.5rem;
            font-size: 0.75rem;
            white-space: nowrap;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'CTASection',
  displayName: 'CTA Cards',
  tokens: {
    'ctasection.card1.title': {
      label: 'Card 1 Title',
      type: 'richtext',
      defaultValue: '<p>Spacious Layouts</p>',
    },
    'ctasection.card1.description': {
      label: 'Card 1 Description',
      type: 'richtext',
      defaultValue:
        '<p>Choose from our spacious <span data-type="field-token" data-field="property_bedrooms_range"></span> bedroom floor plans.</p>',
      description:
        'Supports the property_bedrooms_range field token. At render, the range is expanded into listed bedroom options from available floor plans (e.g. "2" or "Studio, 1, 2 & 3").',
    },
    'ctasection.card2.title': {
      label: 'Card 2 Title',
      type: 'richtext',
      defaultValue: '<p>Picture Yourself Here</p>',
    },
    'ctasection.card2.description': {
      label: 'Card 2 Description',
      type: 'richtext',
      defaultValue: '<p>See what life at our community is all about.</p>',
    },
    'ctasection.card3.title': {
      label: 'Card 3 Title',
      type: 'richtext',
      defaultValue: '<p>Convenience All Around</p>',
    },
    'ctasection.card3.description': {
      label: 'Card 3 Description',
      type: 'richtext',
      defaultValue: '<p>Life is easier at our community.</p>',
    },
  },
  slotGroups: {
    'ctasection.card1_image': card1ImageDef,
    'ctasection.card2_image': card2ImageDef,
    'ctasection.card3_image': card3ImageDef,
    'ctasection.card1_link': card1LinkDef,
    'ctasection.card2_link': card2LinkDef,
    'ctasection.card3_link': card3LinkDef,
  },
};

(CTASection as any).contentSchema = contentSchema;

export default CTASection;
